import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Lock,
  MessageSquare,
  Pin,
  Plus,
  Trash2,
  X,
  History,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  Maximize,
} from "lucide-react";
import DesignDetails from "./components/DesignDetails";
import DesignMemberSelector from "./components/DesignMemberSelector";
import DesignTaskLinkSelector from "./components/DesignTaskLinkSelector";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  approveDesignById,
  createCommentForDesign,
  createPinForDesign,
  deleteCommentById,
  deleteDesignById,
  downloadDiagram,
  getDesignById,
  fetchCommentsForDesign,
  fetchDesignVersions,
  fetchPinsForDesign,
  fetchLinkedTasks,
  getStatusBadgeClass,
  lockDesignById,
  replaceDesignFile,
  updatePinById,
  deletePinById,
  resolvePinById,
  reopenDesign,
} from "../../../api/design/service";
import { useAlert } from "components/UI_components/Alert";
import {
  normalizeFileUrl,
  buildPdfFileSource,
  displayNameFromOrgMember,
  useLoadAssigneesAndApprovers,
  useResizeWidth,
} from "./DesignShared";
// CRA + pdfjs worker: CDN is the most reliable option across bundlers.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
export default function PdfDetailView({
  drawing,
  onBack,
  onUpdate,
  onDelete,
  currentMember,
}) {
  const { addAlert } = useAlert();
  const {
    orgMembers,
    orgMembersLoading,
    designMembers,
    designMembersLoading,
    refreshDesignMembers,
  } = useLoadAssigneesAndApprovers({ drawing, onUpdate });

  const [activePinId, setActivePinId] = useState(null);
  const [isPinMode, setIsPinMode] = useState(false);

  const [pendingPin, setPendingPin] = useState(null);
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinComment, setPinComment] = useState("");
  const [isCreatingPin, setIsCreatingPin] = useState(false);

  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [pinAssignees, setPinAssignees] = useState([]);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvePinId, setResolvePinId] = useState(null);
  const [resolveComment, setResolveComment] = useState("");
  const [showPendingIssuesModal, setShowPendingIssuesModal] = useState(false);

  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfError, setPdfError] = useState(null);

  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [isReplacing, setIsReplacing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [scale, setScale] = useState(1.0);
  const fileInputRef = useRef(null);
  const isLocked = drawing.status === "Locked";
  const isDraft = drawing.status === "Draft";
  const canDesignAdmin =
    currentMember?.design_admin === true ||
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true;

  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [showLinkToTask, setShowLinkToTask] = useState(false);
  const [linkedTasks, setLinkedTasks] = useState([]);
  const siteId = drawing?.site?.id ?? drawing?.site;

  // Local state for comments and pins
  const [comments, setComments] = useState([]);
  const [pins, setPins] = useState([]);

  // Fetch comments
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      try {
        const list = await fetchCommentsForDesign(drawing.id);
        if (!mounted) return;
        const mapped = (list || []).map((c, idx) => ({
          id: c.id || c.pk || `${drawing.id}-c-${idx}`,
          text: c.comment || c.text || "",
          author: c.created_by || "Unknown",
          pin_linked: c.pin || null,
          createdAt: c.created_on || new Date().toISOString(),
          isResolved: !!c.is_resolved || !!c.isResolved || false,
        }));
        setComments(mapped);
      } catch (err) {
        console.error("Failed to fetch comments", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);

  // Fetch pins
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      try {
        const list = await fetchPinsForDesign(drawing.id);
        if (!mounted) return;
        const mapped = (list || []).map((p, idx) => ({
          ...p,
          id: p.id || p.pk || `${drawing.id}-p-${idx}`,
          x:
            typeof p.x_coordinate !== "undefined"
              ? Number(p.x_coordinate)
              : typeof p.x !== "undefined"
                ? Number(p.x)
                : 0,
          y:
            typeof p.y_coordinate !== "undefined"
              ? Number(p.y_coordinate)
              : typeof p.y !== "undefined"
                ? Number(p.y)
                : 0,
          comment: p.comment || p.text || "",
          status: p.status || "pending",
          page_index:
            typeof p.page_index !== "undefined"
              ? Number(p.page_index)
              : typeof p.pageIndex !== "undefined"
                ? Number(p.pageIndex)
                : 1,
          author:
            (p.created_by &&
              (typeof p.created_by === "string"
                ? p.created_by
                : p.created_by.username || p.created_by.name)) ||
            (p.uploaded_by &&
              (typeof p.uploaded_by === "string"
                ? p.uploaded_by
                : p.uploaded_by.username || p.uploaded_by.name)) ||
            p.author ||
            "Unknown",
          createdAt: p.created_at || p.createdAt || new Date().toISOString(),
          assignees: p.assignees || [],
        }));
        setPins(mapped);
      } catch (err) {
        console.error("Failed to fetch pins", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, refreshTick]);

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approveDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Approved" }));
      addAlert("Drawing approved successfully", "success");
    } catch (err) {
      console.error("Approve failed", err);
      addAlert(err?.message || "Failed to approve drawing", "error");
    } finally {
      setIsApproving(false);
    }
  };

  const handleLock = async () => {
    try {
      setIsLocking(true);
      await lockDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), status: "Locked" }));
      setIsPinMode(false);
      addAlert("Drawing locked successfully", "success");
    } catch (err) {
      console.error("Lock failed", err);
      addAlert(err?.message || "Failed to lock drawing", "error");
    } finally {
      setIsLocking(false);
    }
  };

  const pdfUrl = normalizeFileUrl(drawing?.diagram);
  const pdfFile = useMemo(() => buildPdfFileSource(pdfUrl), [pdfUrl]);

  const [isReopening, setIsReopening] = useState(false);

  // Reset page when switching designs/files
  useEffect(() => {
    setPdfPage(1);
    setPdfNumPages(null);
    setPdfError(null);
  }, [drawing?.id, drawing?.diagram]);

  // Fetch versions when modal opens (or design changes)
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showVersionHistory || !drawing?.id) return;
      try {
        setVersionsLoading(true);
        setVersionsError(null);
        const list = await fetchDesignVersions(drawing.id);
        if (!mounted) return;
        setVersions(list);
        onUpdate((prev) => ({ ...(prev || drawing), versions: list }));
      } catch (err) {
        if (!mounted) return;
        console.error("Fetch version history failed", err);
        setVersionsError(err?.message || "Failed to fetch version history");
        setVersions([]);
      } finally {
        if (mounted) setVersionsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [showVersionHistory, drawing.id, refreshTick]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!drawing?.id) return;
      try {
        const list = await fetchLinkedTasks(drawing.id);
        if (!mounted) return;
        setLinkedTasks(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!mounted) return;
        setLinkedTasks([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [drawing?.id, showLinkToTask, refreshTick]);

  const openIssuesCount = pins.filter((p) => p.status === "pending").length;
  const pendingPins = pins.filter((p) => p.status === "pending");

  const handleViewerClick = (e) => {
    if (!isPinMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setShowPinDialog(true);
  };

  const handleCreatePin = async () => {
    if (!pendingPin || !pinComment.trim()) return;
    try {
      setIsCreatingPin(true);
      const payload = {
        x_coordinate: Math.round(pendingPin.x),
        y_coordinate: Math.round(pendingPin.y),
        page_index: pdfPage,
        comment: pinComment.trim(),
        assignees: pinAssignees,
      };
      const created = await createPinForDesign(drawing.id, payload);

      const mapped = {
        id: created.id || created.pk || `p${Date.now()}`,
        x:
          typeof created.x_coordinate !== "undefined"
            ? Number(created.x_coordinate)
            : typeof created.x !== "undefined"
              ? Number(created.x)
              : pendingPin.x,
        y:
          typeof created.y_coordinate !== "undefined"
            ? Number(created.y_coordinate)
            : typeof created.y !== "undefined"
              ? Number(created.y)
              : pendingPin.y,
        comment: created.comment || created.text || pinComment.trim(),
        status: created.status || "pending",
        page_index:
          typeof created.page_index !== "undefined"
            ? Number(created.page_index)
            : typeof created.pageIndex !== "undefined"
              ? Number(created.pageIndex)
              : pdfPage,
        author:
          (created.created_by &&
            (typeof created.created_by === "string"
              ? created.created_by
              : created.created_by.username || created.created_by.name)) ||
          (created.uploaded_by &&
            (typeof created.uploaded_by === "string"
              ? created.uploaded_by
              : created.uploaded_by.username || created.uploaded_by.name)) ||
          created.author ||
          "Unknown",
        createdAt:
          created.created_at || created.createdAt || new Date().toISOString(),
      };

      setPins((prev) => [...prev, mapped]);

      // refresh pins from server (ensures we're in sync)
      try {
        const list = await fetchPinsForDesign(drawing.id);
        const normalized = (list || []).map((p, idx) => ({
          ...p,
          id: p.id || p.pk || `${drawing.id}-p-${idx}`,
          x:
            typeof p.x_coordinate !== "undefined"
              ? Number(p.x_coordinate)
              : typeof p.x !== "undefined"
                ? Number(p.x)
                : 0,
          y:
            typeof p.y_coordinate !== "undefined"
              ? Number(p.y_coordinate)
              : typeof p.y !== "undefined"
                ? Number(p.y)
                : 0,
          comment: p.comment || p.text || "",
          status: p.status || "pending",
          page_index:
            typeof p.page_index !== "undefined"
              ? Number(p.page_index)
              : typeof p.pageIndex !== "undefined"
                ? Number(p.pageIndex)
                : 1,
          author:
            (p.created_by &&
              (typeof p.created_by === "string"
                ? p.created_by
                : p.created_by.username || p.created_by.name)) ||
            (p.uploaded_by &&
              (typeof p.uploaded_by === "string"
                ? p.uploaded_by
                : p.uploaded_by.username || p.uploaded_by.name)) ||
            p.author ||
            "Unknown",
          createdAt: p.created_at || p.createdAt || new Date().toISOString(),
        }));
        setPins(normalized);
      } catch (refreshErr) {
        console.warn("Refresh pins after create failed", refreshErr);
      }

      setPinComment("");
      setPinAssignees([]);
      setPendingPin(null);
      setShowPinDialog(false);
      setIsPinMode(false);
      addAlert("Pin added", "success");
    } catch (err) {
      console.error("Create pin failed", err);
      addAlert(err?.message || "Failed to add pin", "error");
    } finally {
      setIsCreatingPin(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      setIsCreatingComment(true);
      const created = await createCommentForDesign(
        drawing.id,
        commentText.trim(),
      );
      const mapped = {
        id: created.id || created.pk || `c${Date.now()}`,
        text: created.comment || created.text || commentText.trim(),
        author: created.created_by || "Unknown",
        createdAt:
          created.created_at || created.createdAt || new Date().toISOString(),
        isResolved: !!created.is_resolved || !!created.isResolved || false,
      };
      setComments((prev) => [...prev, mapped]);
      setCommentText("");
    } catch (err) {
      console.error("Create comment failed", err);
      addAlert(err?.message || "Failed to add comment", "error");
    } finally {
      setIsCreatingComment(false);
    }
  };
  const handleConfirmDeleteDesign = () => {
    addAlert("Are you sure you want to delete this design?", "confirm", {
      onConfirm: async () => {
        try {
          await deleteDesignById(drawing.id);
          if (onDelete) onDelete(drawing.id);
          addAlert("Design deleted", "success");
          onBack();
        } catch (err) {
          console.error("Delete design failed", err);
          addAlert(err?.message || "Failed to delete design", "error");
        }
      },
    });
  };
  const handleReplaceClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    try {
      setIsReplacing(true);
      const replaced = await replaceDesignFile(drawing.id, file);

      // After replace, hydrate from retrieve API to always get the latest file URL.
      try {
        const fresh = await getDesignById(drawing.id);
        onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));
      } catch (_) {
        const newDiagramRaw =
          replaced?.diagram ||
          replaced?.fileUrl ||
          replaced?.file_url ||
          replaced?.url ||
          drawing.diagram;
        const newDiagram = normalizeFileUrl(newDiagramRaw);
        const updated = {
          ...drawing,
          diagram: newDiagram,
          lastUpdated: (
            replaced?.last_updated_on ||
            replaced?.lastUpdated ||
            drawing.lastUpdated ||
            new Date().toISOString()
          )
            .toString()
            .split("T")[0],
        };
        onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      }
      addAlert("File replaced successfully", "success");
    } catch (err) {
      console.error("Replace file failed", err);
      addAlert(err?.message || "Failed to replace file", "error");
    } finally {
      setIsReplacing(false);
    }
  };

  const handleResolvePin = async (pinId) => {
    setResolvePinId(pinId);
    setResolveComment("");
    setShowResolveModal(true);
  };

  const handleConfirmResolve = async () => {
    if (!resolvePinId) return;
    try {
      await resolvePinById(resolvePinId, { comment: resolveComment.trim() });
      setPins((prev) =>
        prev.map((p) =>
          p.id === resolvePinId ? { ...p, status: "solved" } : p,
        ),
      );
      setResolvePinId(null);
      setShowResolveModal(false);
      setActivePinId(null);
      addAlert("Issue marked as resolved", "success");
    } catch (err) {
      console.error("Resolve pin failed", err);
      addAlert(err?.message || "Failed to resolve pin", "error");
    }
  };
  const handleDeletePin = (pinId) => {
    addAlert("Are you sure you want to delete this pin?", "confirm", {
      onConfirm: async () => {
        try {
          await deletePinById(pinId);
          setPins((prev) => prev.filter((p) => p.id !== pinId));
          setActivePinId(null);
          addAlert("Pin deleted successfully", "success");
        } catch (err) {
          console.error("Delete pin failed", err);
          addAlert(err?.message || "Failed to delete pin", "error");
        }
      },
    });
  };

  const handleReopenClick = async () => {
    try {
      setIsReopening(true);
      const reopened = await reopenDesign(drawing.id);
      const updated = {
        ...drawing,
        status: "Open",
        lastUpdated: new Date().toISOString().split("T")[0],
      };
      onUpdate((prev) => ({ ...(prev || drawing), ...updated }));
      addAlert("Design reopened successfully", "success");
    } catch (err) {
      console.error("Reopen design failed", err);
      addAlert(err?.message || "Failed to reopen design", "error");
    } finally {
      setIsReopening(false);
    }
  };

  const handleViewVersion = (version) => {
    console.log(version);
    if (version.fileType === "pdf") {
      onUpdate((prev) => ({
        ...prev,
        version_number: version.version,
        status: version.status,
        uploaded_by: version.uploadedBy,
        diagram: version.fileUrl,
        file_type: version.fileType,
      }));
    } else if (
      ["image", "jpeg", "png", "jpg", "svg"].includes(version.fileType)
    ) {
      onUpdate((prev) => ({
        ...prev,
        diagram: version.fileUrl,
        file_type: version.fileType,
        version_number: version.version,
        status: version.status,
        uploaded_by: version.uploadedBy,
      }));
    } else if (version.fileType.toLowerCase() === "dwg") {
      onUpdate((prev) => ({
        ...prev,
        diagram: version.fileUrl,
        file_type: version.fileType,
        version_number: version.version,
        status: version.status,
        urn: version.aps_urn,
        uploaded_by: version.uploadedBy,
      }));
    }
    setShowVersionHistory(false);
  };

  const handleShowCurrentVersion = async () => {
    if (!drawing?.id) return;
    try {
      const fresh = await getDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));

      try {
        const list = await fetchLinkedTasks(drawing.id);
        setLinkedTasks(Array.isArray(list) ? list : []);
      } catch (_) {
        // best-effort; actions can still render correctly from fresh status
      }
    } catch (err) {
      console.error("Failed to refresh current version", err);
      addAlert(err?.message || "Failed to load current version", "error");
    }
  };

  const handleRefreshAll = async () => {
    if (!drawing?.id) return;
    try {
      setIsRefreshing(true);
      const fresh = await getDesignById(drawing.id);
      onUpdate((prev) => ({ ...(prev || drawing), ...(fresh || {}) }));
      setRefreshTick((prev) => prev + 1);
      try {
        await refreshDesignMembers?.();
      } catch (_) {
        // best-effort refresh for members
      }
    } catch (err) {
      console.error("Refresh failed", err);
      addAlert(err?.message || "Failed to refresh design data", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div
      className="flex-1 w-full h-[92vh] overflow-y-scroll flex flex-col p-4 pb-0 gap-3 bg-gray-50"
      style={{
        scrollbarWidth: "none",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="px-2 py-1 border rounded  cursor-pointer flex items-center gap-2 hover:bg-sm-accent hover:text-white"
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Design</span>
              <span>{">"}</span>
              <span className="truncate">{drawing.name}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <h1 className="text-lg font-bold truncate">{drawing.name}</h1>
              <span
                className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(drawing.status)}`}
              >
                {drawing.status}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Pending Issues Badge */}
          {openIssuesCount > 0 && (
            <button
              className="flex flex-col border border-red-200 bg-red-50 rounded px-3 py-1.5 hover:bg-red-100"
              onClick={() => setShowPendingIssuesModal(true)}
              type="button"
            >
              <span className="text-xs font-semibold text-red-600">
                {openIssuesCount} Pending Issues
              </span>
              {/* <span className="text-[10px] text-red-500">
                    Resolve before locking
                  </span> */}
            </button>
          )}

          {/* Version History Button */}
          <button
            className="px-2 py-1.5 border rounded text-xs bg-white hover:bg-gray-100 flex items-center gap-1 disabled:opacity-60"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
            title="Refresh"
            type="button"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
          <button
            className="px-3 py-1.5 border rounded text-xs bg-white hover:bg-gray-100 flex items-center gap-1"
            onClick={() => setShowVersionHistory(true)}
            type="button"
          >
            <History className="w-4 h-4" />
            Version History
          </button>
          {drawing.version_number !== drawing.original_version && (
            <button
              className="px-3 py-1.5 border rounded text-xs flex items-center gap-2 bg-white"
              onClick={() => handleShowCurrentVersion()}
            >
              <History className="w-4 h-4" /> Show Current Version
            </button>
          )}
          {!isLocked && (
            <>
              {canDesignAdmin && drawing.status !== "Approved" && (
                <button
                  className={`px-3 py-1.5 text-xs border rounded flex items-center gap-2 bg-white ${isPinMode ? "bg-gray-100" : ""}`}
                  onClick={() => setIsPinMode((v) => !v)}
                >
                  <Pin className="w-4 h-4" /> {isPinMode ? "Cancel" : "Pin"}
                </button>
              )}
              <button
                className="px-3 py-1.5 text-xs border rounded flex items-center gap-2 bg-white"
                onClick={() => setShowCommentsModal(true)}
              >
                <MessageSquare className="w-4 h-4" /> Comments
              </button>
            </>
          )}
          {/* {canDesignAdmin && drawing.status === "Approved" && (
                        <button
                            className="px-2 py-1 border rounded flex items-center gap-2 bg-white disabled:opacity-60"
                            onClick={handleLock}
                            disabled={isLocking}
                        >
                            <Lock className="w-4 h-4" /> {isLocking ? "Locking..." : "Lock"}
                        </button>
                    )} */}
          <button
            className="px-3 py-1.5 text-xs border rounded flex items-center gap-2 bg-white"
            onClick={() => downloadDiagram(pdfUrl, drawing.name)}
          >
            <Download className="w-4 h-4" /> Download
          </button>
          {canDesignAdmin && (
            <button
              className="px-2 py-1 text-red-600 flex items-center gap-2"
              onClick={handleConfirmDeleteDesign}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1">
        {/* Pages */}
        <aside
          className="col-span-2 border rounded bg-white h-[80vh] overflow-y-scroll "
          style={{
            scrollbarWidth: "none",
          }}
        >
          <div className="px-3 py-2 border-b text-sm font-medium">Pages</div>
          <div className="p-2 space-y-2">
            {pdfFile && (
              <Document
                file={pdfFile}
                loading={
                  <div className="text-xs text-gray-500 p-2">Loading…</div>
                }
                onLoadSuccess={({ numPages }) => {
                  setPdfError(null);
                  setPdfNumPages(numPages);
                }}
                onLoadError={(err) => setPdfError(err?.message || String(err))}
                onSourceError={(err) =>
                  setPdfError(err?.message || String(err))
                }
              >
                {pdfError && (
                  <div className="text-xs text-red-600 p-2">
                    Failed to load PDF file.
                    <div className="text-[10px] text-red-500 mt-1 break-words">
                      {pdfError}
                    </div>
                  </div>
                )}

                {Array.from({ length: pdfNumPages || 0 }).map((_, idx) => {
                  const pageNum = idx + 1;
                  const isActive = pageNum === pdfPage;
                  return (
                    <button
                      key={pageNum}
                      className={`w-full border rounded p-1 text-left ${
                        isActive ? "border-purple-500" : "border-gray-200"
                      }`}
                      onClick={() => setPdfPage(pageNum)}
                      type="button"
                    >
                      <div className="text-[10px] text-gray-500 mb-1">
                        Page {pageNum}
                      </div>
                      <div className="bg-gray-50 rounded ">
                        <Page
                          pageNumber={pageNum}
                          width={120}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </div>
                    </button>
                  );
                })}
              </Document>
            )}
          </div>
        </aside>

        {/* Viewer */}
        <main className="col-span-7 border rounded bg-white h-[80vh] flex flex-col">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <div className="text-sm font-medium">
              Viewer (v{drawing.version_number})
              {drawing.original_version === drawing.version_number && (
                <span className="ml-2 text-xs font-medium bg-sm-secondary/10 px-2 py-1 rounded-lg text-sm-secondary">
                  Latest Version
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button
                className="px-2 py-1 border rounded disabled:opacity-50"
                disabled={pdfPage <= 1}
                onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="text-gray-600">
                Page {pdfPage}
                {pdfNumPages ? ` / ${pdfNumPages}` : ""}
              </span>
              <button
                className="px-2 py-1 border rounded disabled:opacity-50"
                disabled={pdfNumPages ? pdfPage >= pdfNumPages : true}
                onClick={() =>
                  setPdfPage((p) =>
                    pdfNumPages ? Math.min(pdfNumPages, p + 1) : p,
                  )
                }
              >
                Next
              </button>

              <div className="flex items-center gap-1 border-l pl-2 ml-1">
                <button
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                  onClick={() => setScale((s) => Math.max(0.25, s - 0.25))}
                  disabled={scale <= 0.25}
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-[11px] font-medium">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                  onClick={() => setScale((s) => Math.min(3, s + 0.25))}
                  disabled={scale >= 3}
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  className="p-1 hover:bg-gray-100 rounded ml-1"
                  onClick={() => setScale(1.0)}
                  title="Reset Zoom"
                >
                  <Maximize className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div
            className="flex-1 overflow-scroll bg-gray-100"
            style={{ scrollbarWidth: "none" }}
          >
            <PdfViewerSurface
              drawing={drawing}
              pdfFile={pdfFile}
              pdfPage={pdfPage}
              pdfError={pdfError}
              setPdfError={setPdfError}
              isPinMode={isPinMode}
              onSurfaceClick={handleViewerClick}
              pins={pins}
              activePinId={activePinId}
              setActivePinId={setActivePinId}
              handleResolvePin={handleResolvePin}
              handleDeletePin={handleDeletePin}
              currentMemberId={currentMember?.id}
              canDesignAdmin={canDesignAdmin}
              scale={scale}
            />
          </div>
        </main>

        {/* Right sidebar (Details/Actions/Issues/Version history) */}
        <aside
          //   style={{
          //     scrollbarWidth: "none",
          //   }}
          //   className="col-span-3 flex flex-col gap-3 h-[78vh] overflow-y-scroll "
          className="col-span-3 flex flex-col gap-3 h-[80vh] overflow-y-scroll"
          style={{
            scrollbarWidth: "none",
          }}
        >
          <DesignDetails
            drawing={drawing}
            designMembers={designMembers}
            designMembersLoading={designMembersLoading}
            orgMembersLoading={orgMembersLoading}
            canDesignAdmin={canDesignAdmin}
            setShowAddAssignee={setShowAddAssignee}
          />
          {drawing.version_number === drawing.original_version && (
            <div className="border rounded p-3 bg-white">
              <div className="text-xs text-gray-500 mb-2">Actions</div>
              <div className="space-y-2">
                {drawing.status !== "Approved" && !isLocked && (
                  <button
                    className="w-full p-2 border rounded disabled:opacity-60 hover:bg-sm-accent hover:text-white"
                    disabled={isReplacing}
                    onClick={handleReplaceClick}
                  >
                    {isReplacing ? "Replacing..." : "Replace File"}
                  </button>
                )}
                {drawing.status !== "Approved" &&
                  !isLocked &&
                  canDesignAdmin && (
                    <button
                      className="w-full p-2 border rounded hover:bg-sm-primary hover:text-white"
                      onClick={handleApprove}
                      disabled={isApproving || isLocking}
                    >
                      {isApproving ? "Approving..." : "Approve"}
                    </button>
                  )}
                {(drawing.status === "Approved" || isLocked) && (
                  <button
                    className="w-full p-2 border rounded disabled:opacity-60"
                    onClick={handleReopenClick}
                    disabled={isReopening || !canDesignAdmin}
                  >
                    {isReopening ? "Reopening..." : "Reopen"}
                  </button>
                )}
                {drawing.status === "Approved" && canDesignAdmin && (
                  <button
                    className="w-full p-2 border rounded bg-sm-primary text-white hover:bg-sm-primary/90"
                    onClick={() => setShowLinkToTask(true)}
                  >
                    {linkedTasks.length > 0
                      ? `${linkedTasks.length} Tasks linked`
                      : "Link to Task"}
                  </button>
                )}
                {/* {!isDraft && drawing.status === "Approved" && (
                                            <button
                                                className="w-full p-2 bg-purple-700 text-white rounded disabled:opacity-60"
                                                onClick={handleLock}
                                                disabled={isLocking}
                                            >
                                                {isLocking ? "Locking..." : "Lock Design"}
                                            </button>
                                        )} */}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
                onChange={handleReplaceSelected}
              />
            </div>
          )}

          {/* {openIssuesCount > 0 && (
            <div className="border rounded p-2 bg-red-50">
              <div className="text-xs text-red-600 font-semibold">
                {openIssuesCount} Pending Issue(s)
              </div>
              <div className="text-xs text-gray-500">
                Resolve before locking
              </div>
            </div>
          )}

          <button
            className="w-full p-2 border rounded mb-3"
            onClick={() => setShowVersionHistory(true)}
          >
            Version History
          </button> */}

          {/* Comments Sidebar Section */}
          {/* <div className="flex-1 flex flex-col border rounded bg-white "> */}
          <div className="flex-1 min-h-0 flex flex-col border rounded bg-white overflow-hidden h-0">
            <div className="px-3 py-2 border-b flex items-center justify-between bg-gray-50/50">
              <div className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-purple-600" />
                Comments
              </div>
              <button
                type="button"
                onClick={() => setShowCommentsModal(true)}
                className="text-purple-600 hover:text-purple-700 text-xs font-medium flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {/* <div
              className="flex-1 p-2 space-y-2 bg-white"
              style={{ scrollbarWidth: "none" }}
            > */}
            <div
              className="flex-1 overflow-y-auto p-2 space-y-2 bg-white"
              style={{ scrollbarWidth: "thin" }}
            >
              {comments.length === 0 ? (
                <div className="text-[11px] text-gray-400 text-center py-4 italic">
                  No comments yet.
                </div>
              ) : (
                comments
                  .slice()
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((c) => (
                    <div
                      key={c.id}
                      className="p-2 border border-gray-100 rounded bg-gray-50/30"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold text-gray-900 truncate max-w-[100px]">
                          {c.author}
                        </span>

                        <div className="flex items-center gap-2">
                          {c.pin_linked && (
                            <span
                              onClick={() => setActivePinId(c.pin_linked)}
                              className="text-sm-primary text-[10px] cursor-pointer bg-sm-primary/10 px-2 py-0.5 rounded "
                            >
                              Linked to Pin #{c.pin_linked}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400">
                            {c.createdAt
                              ? new Date(c.createdAt).toLocaleDateString([], {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 leading-relaxed break-words">
                        {c.text}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Design Member Selector Modal */}
      <DesignMemberSelector
        show={showAddAssignee}
        onClose={() => setShowAddAssignee(false)}
        designId={drawing.id}
        orgMembers={orgMembers}
        orgMembersLoading={orgMembersLoading}
        currentDesignMembers={designMembers}
        onUpdate={refreshDesignMembers}
      />

      <DesignTaskLinkSelector
        show={showLinkToTask}
        onClose={() => setShowLinkToTask(false)}
        designId={drawing.id}
        siteId={siteId}
        onLinked={onUpdate}
      />

      {/* Pin comment modal */}
      {showPinDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-md">
            <h3 className="font-semibold mb-2">Add Comment Pin</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Comment
                </label>
                <textarea
                  placeholder="Enter your comment or issue description..."
                  value={pinComment}
                  onChange={(e) => setPinComment(e.target.value)}
                  rows={3}
                  className="w-full border p-2 rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Assignees
                </label>
                <div className="border rounded p-2 max-h-32 overflow-y-auto space-y-1">
                  {(designMembers || []).map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={pinAssignees.includes(m.member_id || m.id)}
                        onChange={(e) => {
                          const id = m.member_id || m.id;
                          if (e.target.checked) {
                            setPinAssignees((prev) => [...prev, id]);
                          } else {
                            setPinAssignees((prev) =>
                              prev.filter((pid) => pid !== id),
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <span className="text-xs">
                        {displayNameFromOrgMember(m)}
                      </span>
                    </label>
                  ))}
                  {(designMembers || []).length === 0 && (
                    <span className="text-xs text-gray-400">
                      No members to assign
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => {
                  setShowPinDialog(false);
                  setPendingPin(null);
                  setPinComment("");
                  setPinAssignees([]);
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-purple-700 text-white rounded text-sm disabled:opacity-60"
                onClick={handleCreatePin}
                disabled={!pinComment.trim() || isCreatingPin}
              >
                {isCreatingPin ? "Adding..." : "Add Pin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Pin Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200]">
          <div className="bg-white p-4 rounded w-full max-w-md shadow-xl">
            <h3 className="font-semibold mb-2">Resolve Pin</h3>
            <p className="text-xs text-gray-500 mb-2">
              Optional: add a comment about how this was resolved.
            </p>
            <textarea
              placeholder="Resolution notes..."
              value={resolveComment}
              onChange={(e) => setResolveComment(e.target.value)}
              rows={3}
              className="w-full border p-2 rounded text-sm mb-3"
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => {
                  setShowResolveModal(false);
                  setResolvePinId(null);
                  setResolveComment("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                onClick={handleConfirmResolve}
              >
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {showPendingIssuesModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[210]">
          <div className="bg-white p-4 rounded w-full max-w-2xl shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Pending Pins</h3>
              <button
                type="button"
                onClick={() => setShowPendingIssuesModal(false)}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-auto border rounded bg-gray-50 p-2">
              {pendingPins.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-6">
                  No pending pins found.
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingPins.map((pin, idx) => (
                    <button
                      key={pin.id || idx}
                      type="button"
                      className="w-full text-left bg-white border rounded p-2 hover:bg-gray-50"
                      onClick={() => {
                        setActivePinId(pin.id);
                        if (Number(pin.page_index) > 0) {
                          setPdfPage(Number(pin.page_index));
                        }
                        setShowPendingIssuesModal(false);
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-red-600">
                          Pin #{pin.id || idx + 1}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {pin.page_index ? `Page ${pin.page_index}` : ""}
                        </span>
                      </div>
                      <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">
                        {pin.comment || "No comment"}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        {pin.author ? `By: ${pin.author}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end mt-3">
              <button
                className="px-3 py-1 border rounded text-sm"
                onClick={() => setShowPendingIssuesModal(false)}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments modal (PDF flow) */}
      {showCommentsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-2xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Comments</h3>
              <button onClick={() => setShowCommentsModal(false)} type="button">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="max-h-80 overflow-auto border rounded p-2 bg-gray-50">
              {comments.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-6">
                  No comments yet
                </div>
              ) : (
                <div className="space-y-2">
                  {comments.map((c) => (
                    <div key={c.id} className="bg-white border rounded p-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate">
                            {c.author}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
                            {c.text}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-[10px] text-gray-400 flex flex-row items-center gap-2">
                            {c.pin_linked && (
                              <span className="text-sm-primary bg-sm-primary/10 px-2 py-0.5 rounded ">
                                Linked to Pin #{c.pin_linked}
                              </span>
                            )}
                            {c.createdAt
                              ? new Date(c.createdAt).toLocaleDateString()
                              : ""}
                          </div>
                          {/* <button
                            className="text-red-600 p-1"
                            title="Delete comment"
                            onClick={() => handleDeleteComment(c.id)}
                            type="button"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button> */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2">
              <input
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="flex-1 border py-2 px-3 rounded text-sm"
                onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
                disabled={isCreatingComment}
              />
              <button
                className="px-3 py-2 bg-purple-700 text-white rounded text-sm disabled:opacity-60"
                onClick={handleAddComment}
                disabled={isCreatingComment || !commentText.trim()}
              >
                {isCreatingComment ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded w-full max-w-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Version History</h3>
              <button
                onClick={() => setShowVersionHistory(false)}
                type="button"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-auto">
              {versionsLoading && (
                <div className="text-sm text-gray-500 p-3">
                  Loading versions...
                </div>
              )}
              {!versionsLoading && versionsError && (
                <div className="text-sm text-red-600 p-3">{versionsError}</div>
              )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).length === 0 && (
                  <div className="text-sm text-gray-500 p-3">
                    No versions found.
                  </div>
                )}
              {!versionsLoading &&
                !versionsError &&
                (versions || []).map((version) => (
                  <div
                    key={version.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-xs font-bold bg-gray-200 px-2 py-1 rounded shrink-0">
                        {version.version}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{version.name}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {version.uploadedBy || drawing.uploadedBy} •{" "}
                          {version.uploadDate || drawing.lastUpdated}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${getStatusBadgeClass(version.status)}`}
                      >
                        {version.status}
                      </span>
                      <button
                        className="px-2 py-1 text-sm border rounded hover:bg-sm-accent hover:text-white"
                        disabled={!version.fileUrl}
                        onClick={() => handleViewVersion(version)}
                      >
                        View
                      </button>
                      <button
                        className="px-2 py-1 text-sm border rounded disabled:opacity-60"
                        disabled={!version.fileUrl}
                        onClick={() =>
                          downloadDiagram(
                            version.fileUrl,
                            `${drawing.name}_${version.version}`,
                          )
                        }
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
    </div>
  );
}

function PdfViewerSurface({
  drawing,
  pdfFile,
  pdfPage,
  pdfError,
  setPdfError,
  isPinMode,
  onSurfaceClick,
  pins,
  activePinId,
  setActivePinId,
  handleResolvePin,
  handleDeletePin,
  currentMemberId,
  canDesignAdmin,
  scale = 1.0,
}) {
  const { ref, width } = useResizeWidth();

  // react-pdf needs a numeric width; clamp to something reasonable
  const baseWidth = Math.max(320, Math.floor((width || 0) - 32));
  const pageWidth = baseWidth * scale;

  return (
    <div className="bg-gray-50 min-h-full">
      <div ref={ref} className="p-4">
        <div className="min-w-max flex justify-center">
          <div
            className={`relative bg-white border rounded ${isPinMode ? "cursor-crosshair" : ""}`}
            style={{ width: pageWidth }}
            onClick={onSurfaceClick}
          >
            <Document
              file={pdfFile}
              loading={
                <div className="text-sm text-gray-500 p-6">Loading PDF…</div>
              }
              error={
                <div className="text-sm text-red-600 p-6">
                  Failed to load PDF
                  {pdfError ? (
                    <div className="text-xs text-red-500 mt-2 break-words">
                      {pdfError}
                    </div>
                  ) : null}
                </div>
              }
              onLoadSuccess={() => setPdfError(null)}
              onLoadError={(err) => setPdfError(err?.message || String(err))}
              onSourceError={(err) => setPdfError(err?.message || String(err))}
            >
              <Page pageNumber={pdfPage} width={pageWidth} />
            </Document>

            {(pins || [])
              .filter((pin) => {
                const pinPage =
                  typeof pin.page_index !== "undefined"
                    ? Number(pin.page_index)
                    : typeof pin.pageIndex !== "undefined"
                      ? Number(pin.pageIndex)
                      : 1;
                return pinPage === pdfPage;
              })
              .map((pin) => (
              <div
                key={pin.id}
                className="absolute transform -translate-x-1/2 -translate-y-full z-[50]"
                style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              >
                <button
                  className="relative"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Pin clicked", pin);
                    console.log("current member id", currentMemberId);
                    setActivePinId(activePinId === pin.id ? null : pin.id);
                  }}
                  type="button"
                  aria-label="Pin"
                >
                  <Pin
                    className={`w-4 h-4 z-[90] ${pin.status === "solved" ? "text-green-600" : "text-red-600"}`}
                  />
                  {activePinId === pin.id && (
                    <div
                      className={`absolute left-1/2 -translate-x-1/2 w-64 z-[110] ${pin.y < 20 ? "top-full mt-2" : "bottom-full mb-2"}`}
                    >
                      <div className="bg-white border p-2 rounded shadow text-left">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <div className="text-xs font-medium text-gray-700">
                              Pin
                            </div>
                            {pin.status === "solved" ? (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded">
                                SOLVED
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded">
                                PENDING
                              </span>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePinId(null);
                            }}
                            type="button"
                            aria-label="Close"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap mb-1.5">
                          {pin.comment}
                        </p>
                        <div className="text-[10px] text-gray-500 mt-1">
                          {pin.author ? `By: ${pin.author}` : ""}
                        </div>
                        {pin.status === "pending" &&
                          (pin.assignees?.includes(currentMemberId) ||
                            canDesignAdmin) && (
                            <button
                              className="w-full mt-2 p-1 bg-green-600 text-white rounded text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResolvePin(pin.id);
                              }}
                              type="button"
                            >
                              Resolve
                            </button>
                          )}
                        {canDesignAdmin && (
                          <button
                            className="w-full mt-1.5 p-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePin(pin.id);
                            }}
                            type="button"
                          >
                            Delete Pin
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              </div>
            ))}

            {isPinMode && (
              <div className="absolute top-3 left-3 bg-purple-50 text-purple-700 px-2.5 py-1 rounded text-xs">
                Click to place a pin
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
